/**
 * LiveWatch — public attendee page for native webinars.
 * Route: /w/:slug/live  (public, requires registration_id + email in query)
 *
 * Attendee flow:
 *   1. Load webinar meta and check registration
 *   2. Exchange registration_id + email for a viewer token
 *   3. Join the 100ms room
 */
import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { HMSRoomProvider } from "@100mslive/react-sdk";
import axios from "axios";
import { AlertTriangle } from "lucide-react";

import LiveStage from "@/components/live/LiveStage";
import HlsViewer from "@/components/live/HlsViewer";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function LiveWatch() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const registration_id = sp.get("r") || "";
  const email = sp.get("e") || "";
  const isBot = sp.get("bot") === "1";
  const botToken = sp.get("bot_token") || "";

  const [joinInfo, setJoinInfo] = useState(null);
  const [webinar, setWebinar] = useState(null);
  const [error, setError] = useState(null);
  const [emailInput, setEmailInput] = useState(email);
  const [regInput, setRegInput] = useState(registration_id);

  useEffect(() => {
    (async () => {
      try {
        const { data: w } = await axios.get(`${BASE}/api/public/webinar/${slug}`);
        setWebinar(w);
      } catch (e) {
        setError("Webinar not found");
      }
    })();
  }, [slug]);

  const doJoin = async (rid, em) => {
    setError(null);
    try {
      const { data } = await axios.post(`${BASE}/api/public/w/${slug}/live/token`, {
        registration_id: rid,
        email: em,
      });
      // persist for later actions (hand-raise) that need email verification
      try { sessionStorage.setItem(`live-email-${slug}`, em); } catch { /* noop */ }
      setJoinInfo(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  };

  // Bot join: exchange signed bot_token for HMS auth token — no registration needed.
  const doBotJoin = async (bt) => {
    setError(null);
    try {
      const { data } = await axios.post(`${BASE}/api/public/w/${slug}/live/bot-token`, {
        bot_token: bt,
      });
      setJoinInfo(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Bot auth failed");
    }
  };

  useEffect(() => {
    if (isBot && botToken) {
      doBotJoin(botToken);
    } else if (registration_id && email) {
      doJoin(registration_id, email);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registration_id, email, isBot, botToken]);

  if (!joinInfo) {
    return (
      <div className="min-h-screen bg-[#0B0D17] text-white flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white/5 backdrop-blur rounded-2xl p-6 space-y-4">
          <div>
            <div className="text-xs uppercase tracking-widest text-white/60">Live Webinar</div>
            <div className="font-display font-black text-2xl mt-1">{webinar?.title || "Loading…"}</div>
          </div>
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm p-3 rounded flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
          <div className="space-y-2">
            <label className="block text-xs uppercase tracking-widest text-white/60">Registration ID</label>
            <input
              className="w-full bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-white/30"
              value={regInput}
              onChange={(e) => setRegInput(e.target.value)}
              placeholder="Sent to your email"
              data-testid="live-reg-id"
            />
            <label className="block text-xs uppercase tracking-widest text-white/60 mt-2">Email</label>
            <input
              type="email"
              className="w-full bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:border-white/30"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="you@example.com"
              data-testid="live-email"
            />
          </div>
          <button
            onClick={() => doJoin(regInput.trim(), emailInput.trim())}
            className="w-full bg-white text-[#0B0D17] font-bold py-2.5 rounded-lg hover:bg-white/90"
            data-testid="live-join-btn"
          >
            Join Live Session
          </button>
          <Link to={`/w/${slug}`} className="block text-center text-xs text-white/50 hover:text-white/80 mt-2">
            ← Back to webinar page
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      {joinInfo.hls_url ? (
        <HlsViewer
          hlsUrl={joinInfo.hls_url}
          webinar={webinar}
          onExit={() => { window.location.href = `/w/${slug}`; }}
        />
      ) : (
        <HMSRoomProvider>
          <LiveStage
            role="attendee"
            webinar={webinar}
            joinInfo={joinInfo}
            onExit={() => { window.location.href = `/w/${slug}`; }}
          />
        </HMSRoomProvider>
      )}
    </>
  );
}
